import { FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { authenticationBannersCMS as fetchAuthenticationBannersCMS } from '@/api/cms';
import { AuthenticationBanners } from '@/api/cms/authenticationBanners';
import { ImageComponent } from '@/components/Image';
import { Skeleton } from '@/components/Skeleton';
import { getCustomOffer } from '@/hooks/customOffer';
import { useSwrMutation } from '@/hooks/swr';
import { useIsLanding } from '@/hooks/useIsLanding';
import { useCommonStore } from '@/store';

import { ScRegistrationBanner } from '../styled';

export const RegistrationBanner = () => {
  const {
    i18n: { language },
  } = useTranslation();

  const regBannerStore = useCommonStore.use.regBanner();

  const {
    trigger: requestBanners,
    data: banners,
    isMutating: isLoading,
  } = useSwrMutation({
    key: 'fetchAuthenticationBannersCMS',
    fetcher: fetchAuthenticationBannersCMS,
  });

  useEffect(() => {
    requestBanners(language);
  }, [language, requestBanners]);

  if (isLoading || !banners) {
    return (
      <Skeleton.Container
        gap={24}
        flexDirection="column"
        flexGrow={1}
        overflowY="auto"
        marginBottom={32}
        borderRadius={16}
      >
        <Skeleton.Rect height={106} bgColorVariant="light" />
      </Skeleton.Container>
    );
  }

  const regBannerBg =
    regBannerStore?.background || banners.registrationV2?.background?.url;
  const regBannerCharacters =
    regBannerStore?.character || banners.registrationV2?.characters?.url;

  return (
    <ScRegistrationBanner>
      {regBannerBg && (
        <ImageComponent
          src={regBannerBg}
          className="bg"
          priority
          alt="characters"
          fill
        />
      )}

      <div className="offer-container">
        <RegOffer banners={banners} />
      </div>
      {regBannerCharacters && (
        <ImageComponent
          src={regBannerCharacters}
          priority
          alt="characters"
          width={104}
          height={104}
        />
      )}
    </ScRegistrationBanner>
  );
};

const RegOffer: FC<{
  banners: AuthenticationBanners | null;
}> = ({ banners }) => {
  const customOffer = getCustomOffer();
  const availableCrossSell = useCommonStore.use.availableCrossSell();
  const regBannerStore = useCommonStore.use.regBanner();
  const { isLanding } = useIsLanding();

  const [cmsTitle, cmsOffer1, cmsOffer2] = (() => {
    const offer1 = availableCrossSell
      ? banners?.registrationV2?.crossSellOffer_1
      : banners?.registrationV2?.offer_1;

    const offer2 = availableCrossSell
      ? banners?.registrationV2?.crossSellOffer_2
      : banners?.registrationV2?.offer_2;

    return [banners?.registrationV2?.title, offer1, offer2];
  })();

  const [title, offer_1, offer_2] = useMemo(() => {
    return [
      customOffer?.registrationBanner.title || cmsTitle || '',
      (customOffer?.registrationBanner.offer_1 || cmsOffer1 || '').split('+'),
      customOffer?.registrationBanner.offer_1
        ? customOffer?.registrationBanner.offer_2
        : cmsOffer2 || '',
    ];
  }, [
    customOffer?.registrationBanner.title,
    customOffer?.registrationBanner.offer_1,
    customOffer?.registrationBanner.offer_2,
    cmsTitle,
    cmsOffer1,
    cmsOffer2,
  ]);

  const currentTitle = regBannerStore?.title || title;
  const currentOffer1 = regBannerStore?.offer1 || offer_1;
  const currentOffer2 =
    isLanding && regBannerStore ? regBannerStore?.offer2 : offer_2;

  return (
    <>
      <h2>{currentTitle}</h2>
      <p className="offer-1">
        {currentOffer1?.[0]}{' '}
        {currentOffer1?.[1] && <span>+ {currentOffer1?.[1]}</span>}
      </p>
      {currentOffer2 && <p className="offer-2">{currentOffer2}</p>}
    </>
  );
};
